import { err, ok, Result } from 'neverthrow';

import { NetworkError } from '@rover/rsdk/src/errors';

import { Logger } from '../../Logging';
import { IMAGE_UPLOAD_MAX_FILE_SIZE } from '../config';
import { MediaType } from '../types';

import { uploadFile } from './internals';
import { attach, mediaUploadAuthCreate, MediaUploadAuthResponseCamel } from './internals.common';
import MediaUploadError, {
  MediaUploadMaxFileSizeError,
  MediaUploadNetworkError,
  MediaUploadNotAuthorizedError,
  MediaUploadServerError,
  MediaUploadUnknownError,
} from './MediaUploadError';
import UploadTask, { AttachableUploadTask } from './UploadTask';

export default class UploadWorker {
  currentTask?: UploadTask;

  constructor() {
    this.currentTask = undefined;
  }

  public startTask(task: UploadTask): boolean {
    if (this.currentTask) return false;

    // if the task was cancelled while waiting in the queue, it is considered finished
    if (task.isCancelled()) {
      this.handleTaskFinish();
      return false;
    }

    this.currentTask = task;
    this.currentTask.attachListeners({
      onFinish: this.handleTaskFinish,
      onRetry: this.handleTaskRetry,
      onCancel: this.handleTaskFinish,
    });

    UploadWorker.runUploadTask(this.currentTask);

    return true;
  }

  private handleTaskRetry = (): void => {
    if (!this.currentTask) return;

    // if the task was cancelled while waiting to retry, it is considered finished
    if (this.currentTask.isCancelled()) {
      this.handleTaskFinish();
      return;
    }

    UploadWorker.runUploadTask(this.currentTask);
  };

  // when a task is finished, free up this worker
  private handleTaskFinish = (): void => {
    this.currentTask = undefined;
  };

  static async runUploadTask(task: UploadTask): Promise<void> {
    const { type: mediaType, size } = task.file;
    Logger.debug('rsdk.media.uploadMedia.start', { mediaType });
    // Metrics.increment('rsdk.media.uploadMedia.start');
    // const startTime = new Date();

    if (task.isCancelled()) {
      Logger.debug('rsdk.media.uploadMedia.uploadCancelled', { mediaType });
      return;
    }

    task.start(); // updates task status and calls listeners

    // Step 0: enforce file size limit for images
    //         videos don't have a size limit, at least not one defined in our codebase
    if (mediaType === MediaType.IMAGE && size > IMAGE_UPLOAD_MAX_FILE_SIZE) {
      task.fail(new MediaUploadMaxFileSizeError());
      return;
    }

    // Step 1: Authorization
    const authRes = await UploadWorker.auth(task);

    if (authRes.isOk()) {
      Logger.debug('rsdk.media.uploadMedia.authSuccess', { mediaType });
      // Metrics.increment('rsdk.media.uploadMedia.authSuccess');

      // Step 2: Upload to S3
      const { uploadUrl } = authRes.value;
      const uploadRes = await UploadWorker.upload(task, uploadUrl);

      if (uploadRes.isOk()) {
        if (task.isCancelled()) {
          Logger.debug('rsdk.media.uploadMedia.uploadCancelled', { mediaType });
          return;
        }

        Logger.debug('rsdk.media.uploadMedia.uploadSuccess', { mediaType });
        // Metrics.increment('rsdk.media.uploadMedia.uploadSuccess');
        // Metrics.histogram('rsdk.media.uploadMedia.uploadTime', new Date() - startTime);

        // split the query params off before passing the image url back as a result
        // otherwise GET-ing the image fails for amazon server reasons
        const finalUploadUrl = uploadUrl.split('?')[0];

        if (task.isAttachable()) {
          // Step 3 (optional): Attach uploaded image to the content object
          const attachRes = await UploadWorker.attach(task);

          if (attachRes.isOk()) {
            Logger.debug('rsdk.media.uploadMedia.attachSuccess', { mediaType });
            // Metrics.increment('rsdk.media.uploadMedia.attachSuccess');

            task.complete(finalUploadUrl);
          } else {
            // Metrics.increment('rsdk.media.uploadMedia.error', { tags: { errorType: 'attach' }});

            task.fail(attachRes.error);
            Logger.error(attachRes.error, { mediaType });
          }
        } else {
          task.complete(finalUploadUrl);
        }
      } else {
        // NOTE(Ryan) I think this metric is granular enough given that if we
        //            see it spike we can dig into the logs^^^ for details
        // Metrics.increment('rsdk.media.uploadMedia.error', { tags: { errorType: 's3-upload' }});

        task.fail(uploadRes.error);
        // Only report to Sentry for non-retryable errors — retryable errors
        // (network) will be retried and reporting each attempt is noise
        if (uploadRes.error.retryable) {
          Logger.info('rsdk.media.uploadMedia.retryableError', { mediaType });
        } else {
          Logger.error(uploadRes.error, { mediaType });
        }
      }
    } else {
      // NOTE(Ryan) I think this metric is granular enough given that if we
      //            see it spike we can dig into the logs^^^ for details
      // Metrics.increment('rsdk.media.uploadMedia.error', { tags: { errorType: 'auth' }});

      task.fail(authRes.error);
      if (authRes.error.retryable) {
        Logger.info('rsdk.media.uploadMedia.retryableAuthError', { mediaType });
      } else {
        Logger.error(authRes.error, { mediaType });
      }
    }
  }

  static async auth(
    task: UploadTask
  ): Promise<Result<MediaUploadAuthResponseCamel, MediaUploadError>> {
    const res = await mediaUploadAuthCreate(task.file, task.options);
    if (res.isOk()) {
      return ok(res.value.body);
    }
    if (res.error instanceof NetworkError) {
      return err(new MediaUploadNetworkError());
    }
    if (res.error.statusCode === 401) {
      return err(new MediaUploadNotAuthorizedError());
    }
    if (res.error.statusCode.toString().startsWith('5')) {
      return err(new MediaUploadServerError());
    }
    return err(
      new MediaUploadUnknownError(
        new Error(`Unknown error during ${task.file.type} upload authorization`)
      )
    );
  }

  static async upload(task: UploadTask, url: string): Promise<Result<true, MediaUploadError>> {
    return uploadFile(task, url);
  }

  static async attach(task: AttachableUploadTask): Promise<Result<true, MediaUploadError>> {
    return attach(task);
  }
}
